import { addDays, isoWeekMonday, previousIsoWeek } from "./days";

export type BodyMeasurementTypeKey = "weight" | "abdomen";
export type BodyMeasurementUnit = "kg" | "cm";

export interface BodyMeasurementType {
  key: BodyMeasurementTypeKey;
  unit: BodyMeasurementUnit;
  label: string;
}

export const BODY_MEASUREMENT_CATALOG: readonly BodyMeasurementType[] = [
  { key: "weight", unit: "kg", label: "Vekt" },
  // Widest circumference around the belly — not the waist (CONTEXT.md).
  { key: "abdomen", unit: "cm", label: "Magemål" },
];

/** Catalog-order subset for a config's selected keys. */
export function catalogTypesFor(
  keys: BodyMeasurementTypeKey[],
): BodyMeasurementType[] {
  return BODY_MEASUREMENT_CATALOG.filter((t) => keys.includes(t.key));
}

/**
 * The single measurement a trend widget graphs.
 *
 * Tolerant of the pre-split `types: [...]` shape by necessity, not for
 * convenience: widget configs seal into week snapshots, and sealed weeks
 * are immutable history that can never be rewritten. A legacy config
 * resolves to its first type in catalog order.
 */
export function trendMeasurementType(config: {
  measurementType?: BodyMeasurementTypeKey;
  types?: BodyMeasurementTypeKey[];
}): BodyMeasurementType {
  const key =
    config.measurementType ?? catalogTypesFor(config.types ?? [])[0]?.key;
  return (
    BODY_MEASUREMENT_CATALOG.find((t) => t.key === key) ??
    BODY_MEASUREMENT_CATALOG[0]
  );
}

/** Configured axis range, tolerant of the pre-split per-unit map. */
export function trendAxisRange(
  config: {
    axisRange?: BodyMeasurementAxisRange;
    axisRanges?: Partial<Record<BodyMeasurementUnit, BodyMeasurementAxisRange>>;
  },
  unit: BodyMeasurementUnit,
): BodyMeasurementAxisRange | undefined {
  return config.axisRange ?? config.axisRanges?.[unit];
}

export interface BodyMeasurementEntryData {
  date: string; // YYYY-MM-DD — duplicated from the id for range reads
  values: Partial<Record<BodyMeasurementTypeKey, number>>;
}

export interface BodyMeasurementAxisRange {
  minimum: number;
  maximum: number;
}

export const BODY_MEASUREMENT_TYPE = "body_measurement";

// Exactly 64 chars ("body_measurement:" + 36-char uuid + ":" + 10-char
// date) — the RxDB id maxLength. The prefix must never be lengthened.
export function bodyMeasurementId(userId: string, date: string): string {
  return `body_measurement:${userId}:${date}`;
}

const TREND_WEEK_COUNT = 12;

export interface TrendWeek {
  year: number;
  weekNumber: number;
  monday: string; // YYYY-MM-DD
}

/** The trailing 12 ISO weeks ending at (year, weekNumber), oldest first. */
export function trendWindow(year: number, weekNumber: number): TrendWeek[] {
  const weeks: TrendWeek[] = [];
  let current = { year, weekNumber };
  for (let i = 0; i < TREND_WEEK_COUNT; i++) {
    weeks.unshift({
      year: current.year,
      weekNumber: current.weekNumber,
      monday: isoWeekMonday(current.year, current.weekNumber),
    });
    current = previousIsoWeek(current.year, current.weekNumber);
  }
  return weeks;
}

/** Inclusive date range covered by the trend window. */
export function trendWindowRange(
  year: number,
  weekNumber: number,
): { startDate: string; endDate: string } {
  const weeks = trendWindow(year, weekNumber);
  return {
    startDate: weeks[0].monday,
    endDate: addDays(weeks[weeks.length - 1].monday, 6),
  };
}

export interface WeeklyAggregate {
  average: number;
  minimum: number;
  maximum: number;
  count: number;
}

/**
 * One aggregate per trend week (index-aligned with `weeks`), or null for
 * weeks without any entry carrying this type — a gap in the chart.
 */
export function aggregateWeekly(
  entries: BodyMeasurementEntryData[],
  typeKey: BodyMeasurementTypeKey,
  weeks: TrendWeek[],
): Array<WeeklyAggregate | null> {
  return weeks.map((week) => {
    const sunday = addDays(week.monday, 6);
    const values = entries
      .filter((e) => e.date >= week.monday && e.date <= sunday)
      .map((e) => e.values[typeKey])
      .filter((v): v is number => typeof v === "number");
    if (values.length === 0) return null;
    return {
      average: values.reduce((s, v) => s + v, 0) / values.length,
      minimum: Math.min(...values),
      maximum: Math.max(...values),
      count: values.length,
    };
  });
}

export interface TrendSummary {
  latest: { value: number; date: string } | null;
  // Last measured week's average minus the first measured week's, within
  // the window; null when fewer than two weeks have measurements.
  change: number | null;
  // change ÷ weeks between the first and last measured weeks.
  changePerWeek: number | null;
  // Extremes over the whole window, taken from individual entries rather
  // than weekly averages: "my lowest weight" means a reading that actually
  // happened, not the average of the week it fell in. Present as soon as
  // anything is measured, unlike change.
  lowest: number | null;
  highest: number | null;
}

export function summarizeTrend(
  entries: BodyMeasurementEntryData[],
  typeKey: BodyMeasurementTypeKey,
  weeks: TrendWeek[],
): TrendSummary {
  const dated = entries
    .filter((e) => typeof e.values[typeKey] === "number")
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  const last = dated[dated.length - 1];
  const latest = last
    ? { value: last.values[typeKey] as number, date: last.date }
    : null;
  const values = dated.map((e) => e.values[typeKey] as number);
  const lowest = values.length > 0 ? Math.min(...values) : null;
  const highest = values.length > 0 ? Math.max(...values) : null;

  const aggregates = aggregateWeekly(entries, typeKey, weeks);
  const measuredIndexes = aggregates
    .map((a, i) => (a === null ? -1 : i))
    .filter((i) => i >= 0);
  if (measuredIndexes.length < 2) {
    return { latest, change: null, changePerWeek: null, lowest, highest };
  }
  const firstIndex = measuredIndexes[0];
  const lastIndex = measuredIndexes[measuredIndexes.length - 1];
  const first = aggregates[firstIndex] as WeeklyAggregate;
  const final = aggregates[lastIndex] as WeeklyAggregate;
  const change = final.average - first.average;
  return {
    latest,
    change,
    changePerWeek: change / (lastIndex - firstIndex),
    lowest,
    highest,
  };
}

/**
 * Deterministic nb-style number: comma decimal separator, up to
 * `maxDecimals` decimals, trailing zeros stripped ("84,2", "84", "-0,67").
 */
export function formatMeasurementValue(value: number, maxDecimals = 1): string {
  const fixed = value.toFixed(maxDecimals);
  const trimmed = fixed.includes(".")
    ? fixed.replace(/0+$/, "").replace(/\.$/, "")
    : fixed;
  // -0 → 0
  const normalized = trimmed === "-0" ? "0" : trimmed;
  return normalized.replace(".", ",");
}

/** "25. juli" for a YYYY-MM-DD date. */
export function formatEntryDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("nb-NO", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** YYYY-MM-DD of a Date in local time (the diary's notion of "today"). */
export function localDateOf(date: Date): string {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${date.getFullYear()}-${month < 10 ? `0${month}` : month}-${
    day < 10 ? `0${day}` : day
  }`;
}

/** Comma- and dot-tolerant input parse; null for blank or invalid. */
export function parseMeasurementInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}
